// Shared helpers for workspace/project member API responses.
//
// The members endpoints return data in several shapes (bare array, or wrapped
// under members/project_members/items/results/data, and sometimes users), and
// member objects carry the user id / profile fields under inconsistent keys.
// These helpers centralise the normalisation that was previously duplicated
// across Participants, ShareProjectModal, ProjectMembersPanel, UsersPicker and
// the Project page.

// Unwrap a list payload regardless of which envelope key the backend used.
// `users` is checked first so user-list endpoints keep working too.
export const extractList = (data: any): any[] => {
    if (Array.isArray(data)) return data
    if (Array.isArray(data?.users)) return data.users
    if (Array.isArray(data?.members)) return data.members
    if (Array.isArray(data?.project_members)) return data.project_members
    if (Array.isArray(data?.items)) return data.items
    if (Array.isArray(data?.results)) return data.results
    if (Array.isArray(data?.data)) return data.data
    return []
}

// User id for a generic list item (user, workspace member, or picker entry).
// Falls back to the item's own `id` when no user-scoped id is present.
export const getUserId = (item: any) =>
    String(item?.user_id ?? item?.userId ?? item?.profile?.user_id ?? item?.profile?.id ?? item?.user?.user_id ?? item?.user?.id ?? item?.id ?? '')

export const getUserIdentityValues = (item: any) => {
    const sources = [item, item?.profile, item?.user, item?.user?.profile]
    const ids = sources.flatMap((source) => [
        source?.user_id,
        source?.userId,
        source?.id,
        source?.corid,
        source?.cor_id,
    ])
        .filter((value) => value !== undefined && value !== null && String(value) !== '')
        .map((value) => `id:${String(value)}`)
    const emails = sources
        .map((source) => source?.email)
        .filter((value) => typeof value === 'string' && value.trim())
        .map((value) => `email:${value.trim().toLowerCase()}`)

    return Array.from(new Set([...ids, ...emails]))
}

export const usersHaveSameIdentity = (left: any, right: any) => {
    const leftValues = new Set(getUserIdentityValues(left))
    return getUserIdentityValues(right).some((value) => leftValues.has(value))
}

// User id for a project-member record (also accepts the camelCase `userId`).
export const getProjectMemberUserId = (projectMember: any) =>
    String(projectMember?.user_id ?? projectMember?.userId ?? projectMember?.user?.user_id ?? projectMember?.user?.id ?? '')

// User id for a workspace-member record. Unlike getUserId, this deliberately
// does NOT fall back to the record's own `id` (which is the membership id).
export const getWorkspaceMemberUserId = (member: any) =>
    String(member?.user_id ?? member?.user?.user_id ?? member?.user?.id ?? '')

// An invited workspace member can exist before the invited person creates an
// account. Such records either carry an explicit pending flag/status or have no
// user id yet, so they cannot be sent to project-member endpoints.
export const isPendingWorkspaceMember = (member: any) => {
    const pendingFlags = [member?.is_pending_invitation, member?.pending_registration]
    if (pendingFlags.some((flag) => flag === true || String(flag).toLowerCase() === 'true')) {
        return true
    }

    const invitationStatus = String(
        member?.invitation_status ?? member?.invitation?.status ?? '',
    ).toLowerCase()

    if (['pending', 'invited', 'sent', 'awaiting_registration'].includes(invitationStatus)) {
        return true
    }

    return getWorkspaceMemberUserId(member) === ''
}

// Merge a project-member record with its matching workspace member into a flat
// user object usable by pickers and avatar lists. Returns null when the record
// has no resolvable user id.
export const normalizeProjectMember = (projectMember: any, workspaceMember: any) => {
    const userId = getProjectMemberUserId(projectMember)

    if (!userId) {
        return null
    }

    const source = workspaceMember || projectMember
    const nested = source?.user || projectMember?.user || {}
    const fullName = source?.name || projectMember?.name || nested?.name || ''
    const [firstName = '', lastName = ''] = fullName.trim().split(/\s+/, 2)

    return {
        ...nested,
        ...source,
        id: userId,
        user_id: userId,
        email: source?.email || nested?.email || projectMember?.email || '',
        first_name: source?.first_name || nested?.first_name || projectMember?.first_name || firstName,
        last_name: source?.last_name || nested?.last_name || projectMember?.last_name || lastName,
        avatar: source?.avatar || source?.photo || source?.photo_data_url || nested?.avatar || nested?.photo || '',
        photo: source?.photo || source?.avatar || source?.photo_data_url || nested?.photo || nested?.avatar || '',
    }
}

// Keep selected users at the top while preserving the task selection order
// and the original order of all unselected users.
export const sortUsersBySelection = (users: any[], selectedUserIds: (string | number)[]) => {
    const selectedPositions = new Map(
        selectedUserIds.map((userId, index) => [String(userId), index]),
    )

    return users
        .map((currentUser, originalIndex) => ({ currentUser, originalIndex }))
        .sort((left, right) => {
            const leftPosition = selectedPositions.get(getUserId(left.currentUser))
            const rightPosition = selectedPositions.get(getUserId(right.currentUser))
            const leftSelected = leftPosition !== undefined
            const rightSelected = rightPosition !== undefined

            if (leftSelected && rightSelected) {
                return leftPosition - rightPosition
            }
            if (leftSelected) return -1
            if (rightSelected) return 1
            return left.originalIndex - right.originalIndex
        })
        .map(({ currentUser }) => currentUser)
}

// Flatten a user-ish object (an API user, a picker entry, or the `useUser`
// value, which nests the real fields under `profile` and/or `user`) into a flat
// record with the identity/display fields at the top level. Outer keys win over
// nested ones, and `overrides` (e.g. the same person as returned by a list
// endpoint) win over everything, falling back per-field when the override
// omits one. Returns null when no user id can be resolved.
export const normalizeUserEntry = (source: any, overrides?: any) => {
    // A project/workspace list entry is authoritative for mutations. Auth
    // profiles can identify the same person with a profile-specific id.
    const userId = getUserId(overrides) || getUserId(source)

    if (!userId) {
        return null
    }

    const profile = source?.profile || {}
    const nested = source?.user || {}
    const pick = (...keys: string[]) => {
        for (const candidate of [overrides, profile, source, nested]) {
            for (const key of keys) {
                if (candidate?.[key]) return candidate[key]
            }
        }
        return ''
    }

    return {
        ...nested,
        ...source,
        ...profile,
        ...(overrides || {}),
        id: userId,
        user_id: userId,
        first_name: pick('first_name'),
        last_name: pick('last_name'),
        email: pick('email'),
        avatar: pick('avatar', 'photo'),
        photo: pick('photo', 'avatar'),
    }
}
